import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckSquare,
  Clock,
  User,
  Building2,
  FileText,
  ChevronRight,
  UserCheck,
  UserMinus,
  Inbox,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useReviewQueue, useClaimReview, useUnassignReview, type ReviewQueueProject } from '@/hooks/useReviews';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

export function Reviews() {
  const { user } = useAuth();
  const { data, isLoading } = useReviewQueue();
  const claimReview = useClaimReview();
  const unassignReview = useUnassignReview();

  const { myReviews, unassigned, others } = useMemo(() => {
    const projects = data?.projects || [];
    return {
      myReviews: projects.filter((p) => p.reviewerId === user?.id),
      unassigned: projects.filter((p) => !p.reviewerId),
      others: projects.filter((p) => p.reviewerId && p.reviewerId !== user?.id),
    };
  }, [data, user]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card p-8 text-center text-gray-500">
        Failed to load review queue
      </div>
    );
  }

  const { summary } = data;

  const handleClaim = (id: string) => {
    claimReview.mutate(id);
  };

  const handleUnassign = (id: string) => {
    unassignReview.mutate(id);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">
          Reviews
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Projects awaiting review and approval
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <SummaryCard icon={CheckSquare} label="Total in Queue" value={summary.total.toString()} iconColor="text-xtravirt-cyan" />
        <SummaryCard icon={Inbox} label="Unassigned" value={summary.unassigned.toString()} iconColor="text-amber-600" />
        <SummaryCard icon={Clock} label="Submitted" value={summary.submitted.toString()} iconColor="text-blue-600" />
        <SummaryCard icon={UserCheck} label="In Review" value={summary.inReview.toString()} iconColor="text-purple-600" />
      </div>

      {/* My Reviews */}
      {myReviews.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-semibold text-xtravirt-dark mb-3">
            My Reviews ({myReviews.length})
          </h2>
          <div className="space-y-2">
            {myReviews.map((p) => (
              <ReviewRow
                key={p.id}
                project={p}
                currentUserId={user?.id}
                onUnassign={() => handleUnassign(p.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Unassigned queue */}
      <section>
        <h2 className="font-heading text-lg font-semibold text-xtravirt-dark mb-3">
          Unassigned ({unassigned.length})
        </h2>
        {unassigned.length === 0 ? (
          <div className="card p-6 text-center text-sm text-gray-500">
            No unassigned projects in the queue.
          </div>
        ) : (
          <div className="space-y-2">
            {unassigned.map((p) => (
              <ReviewRow
                key={p.id}
                project={p}
                currentUserId={user?.id}
                onClaim={() => handleClaim(p.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Assigned to others */}
      {others.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-semibold text-xtravirt-dark mb-3">
            Assigned to Others ({others.length})
          </h2>
          <div className="space-y-2">
            {others.map((p) => (
              <ReviewRow key={p.id} project={p} currentUserId={user?.id} />
            ))}
          </div>
        </section>
      )}

      {summary.total === 0 && (
        <div className="card p-12 text-center">
          <CheckSquare className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">Nothing to review</h3>
          <p className="text-sm text-gray-500">
            Projects appear here when they're submitted for approval.
          </p>
        </div>
      )}
    </div>
  );
}

// ============ Summary Card ============

interface SummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  iconColor: string;
}

function SummaryCard({ icon: Icon, label, value, iconColor }: SummaryCardProps) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <Icon className={cn('h-8 w-8 flex-shrink-0', iconColor)} />
        <div className="min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
          <p className="text-2xl font-heading font-bold text-xtravirt-dark mt-0.5">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============ Review Row ============

interface ReviewRowProps {
  project: ReviewQueueProject;
  currentUserId?: string;
  onClaim?: () => void;
  onUnassign?: () => void;
}

function ReviewRow({ project, currentUserId, onClaim, onUnassign }: ReviewRowProps) {
  const daysWaiting = Math.floor(
    (Date.now() - new Date(project.submittedAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  const isMine = project.reviewerId === currentUserId;

  return (
    <div className="card p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-xtravirt-cyan/10 flex items-center justify-center flex-shrink-0">
        <FileText className="h-5 w-5 text-xtravirt-cyan" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/projects/${project.id}`}
            className="font-medium text-xtravirt-dark hover:text-xtravirt-cyan transition-colors"
          >
            {project.name}
          </Link>
          <span
            className={cn(
              'inline-block px-2 py-0.5 rounded text-xs font-medium',
              project.status === 'submitted'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-purple-100 text-purple-700'
            )}
          >
            {project.status === 'submitted' ? 'Submitted' : 'In Review'}
          </span>
          {daysWaiting > 3 && (
            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
              <Clock className="h-3 w-3" />
              {daysWaiting}d waiting
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            {project.customerName}
          </span>
          {project.submitterName && (
            <span className="flex items-center gap-1">
              Submitted by {project.submitterName}
            </span>
          )}
          {project.reviewerName && (
            <span className="flex items-center gap-1 text-purple-700">
              <User className="h-3 w-3" />
              Reviewer: {project.reviewerName}
            </span>
          )}
        </div>
      </div>

      <div className="text-right flex-shrink-0">
        {project.totalCost > 0 && (
          <p className="font-semibold text-green-700 text-sm">
            {formatCurrency(project.totalCost, project.currency)}
          </p>
        )}
        <p className="text-xs text-gray-500">
          {(project.totalAdjustedDays / 100).toFixed(0)} days
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {!project.reviewerId && onClaim && (
          <button
            onClick={onClaim}
            className="btn-primary text-sm"
            title="Claim for review"
          >
            <UserCheck className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">Claim</span>
          </button>
        )}
        {isMine && onUnassign && (
          <button
            onClick={onUnassign}
            className="btn-secondary text-sm"
            title="Unassign myself"
          >
            <UserMinus className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">Unassign</span>
          </button>
        )}
        <Link
          to={`/projects/${project.id}`}
          className="btn-secondary text-sm"
        >
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
